import { useEffect, useState } from "react";
import { Modal, ModalBody, ModalHeader, ModalContent } from "@heroui/react";
import {
  EditableSelectComponent,
  renderSection,
} from "components";
import { AlertInfo, Checkmark, Edit, Settings, Wrench } from "assets/icons";
import {
  RoofDto,
  MainOrAnnex,
  RoofType,
  RoofSubtype,
  RoofSkeleton,
  RoofOrientation,
  Covering,
  GutterMaterial,
  Insulated,
  SolarPanelsPresent,
  SolarPanelsPossible,
  BuildingParticleStatus,
} from "../../../../shared/types/roof";
import ButtonNew from "components/buttonNew/buttonNew";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import SearchComponent, {
  SearchMode,
} from "shared/components/searchBar/components/searchComponent";
import { ResourceType } from "shared/types/resource";
import { ContactTags } from "shared/types/contacts";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css"; // Base styling

// Type assertion for React 18 compatibility
const DatePicker = ReactDatePicker as any;

import { ImagesContainer } from "shared/components/imagesContainer/imagesContainer";
import { FileLabel } from "shared/types/file";

interface ModalRoofProps {
  isOpen: boolean;
  onClose: () => void;
  building: any;
  onRoofAdded: () => void;
  roof: any;
}

const ModalRoof = ({
  isOpen,
  onClose,
  building,
  onRoofAdded,
  roof,
}: ModalRoofProps) => {
  const [roofData, setRoofData] = useState<RoofDto>({} as RoofDto);
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { session } = useSession();

  useEffect(() => {
    console.log(roof);
    setRoofData(roof || ({} as RoofDto));
    setEditMode(!roof);
    setIsLoading(false);
  }, [roof]);

  // Function to handle changes in input/select fields
  const handleChange = (key: keyof RoofDto, value: any) => {
    setRoofData((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  // Define the shared properties for the roof modal
  const sharedRoofProps = {
    editMode: editMode,
    isLoading: isLoading,
    onEditClick: () => {
      /* ... */
    },
    onSaveClick: () => {
      console.log("Saved data:", roofData);
      onClose();
    },
    originalState: roofData,
    editedState: roofData,
    setStateFunction: setRoofData,
  };

  const createContactFieldComponent = (
    label: any,
    key: any,
    resourceFilter: any,
    metadataFilter: any,
  ) => {
    return () => (
      <div className={editMode ? "grow p-2" : ""}>
        <SearchComponent
          type="regular"
          label={label}
          resourceFilter={resourceFilter}
          metadataFilter={metadataFilter}
          onSelect={(resource) =>
            setRoofData((prev: any) => ({
              ...prev,
              [key]: {
                resourceId: resource.resourceId,
                name: resource.name,
                type: resource.type,
              },
            }))
          }
          initialValue={
            (roofData as any)?.[key] ? (roofData as any)?.[key].name : null
          }
          mode={SearchMode.SEARCH_AND_PRESENT}
          editMode={editMode}
        />
      </div>
    );
  };

  const roofFieldsConfig = [
    {
      label: "Status",
      key: "status",
      Component: () => (
        <EditableSelectComponent
          label="Status"
          editMode={editMode}
          value={roofData.status || ""}
          options={Object.values(BuildingParticleStatus).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("status", value)}
        />
      ),
    },
    {
      label: "Hoofddak/Zijdak",
      key: "mainOrAnnex",
      Component: () => (
        <EditableSelectComponent
          label="Hoofddak/Zijdak"
          editMode={editMode}
          value={roofData.mainOrAnnex || ""}
          options={Object.values(MainOrAnnex).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("mainOrAnnex", value)}
        />
      ),
    },
    {
      label: "Beschrijving (onderscheid tussen eventueel andere daken)",
      key: "description",
    },
    {
      label: "Type",
      key: "roofType",
      Component: () => (
        <EditableSelectComponent
          label="Type"
          editMode={editMode}
          value={roofData.type || ""}
          options={Object.values(RoofType).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("type", value)}
        />
      ),
    },
    {
      label: "Subtype",
      key: "subType",
      Component: () => (
        <EditableSelectComponent
          label="Subtype"
          editMode={editMode}
          value={roofData.subtype || ""}
          options={Object.values(RoofSubtype).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("subtype", value)}
        />
      ),
    },
    {
      label: "Dakskelet",
      key: "roofSkeleton",
      Component: () => (
        <EditableSelectComponent
          label="Dakskelet"
          editMode={editMode}
          value={roofData.skeleton || ""}
          options={Object.values(RoofSkeleton).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("skeleton", value)}
        />
      ),
    },
    {
      label: "Orientatie dakvlak",
      key: "roofOrientation",
      Component: () => (
        <EditableSelectComponent
          label="Orientatie dakvlak"
          editMode={editMode}
          value={roofData.orientation || ""}
          options={Object.values(RoofOrientation).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("orientation", value)}
        />
      ),
    },
    { label: "Oppervlakte", key: "surfaceArea" },
    {
      label: "Bedekking",
      key: "roofCovering",
      Component: () => (
        <EditableSelectComponent
          label="Bedekking"
          editMode={editMode}
          value={roofData.covering || ""}
          options={Object.values(Covering).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("covering", value)}
        />
      ),
    },
    {
      label: "Zonnepanelen aanwezig?",
      key: "solarPanelsPresent",
      Component: () => (
        <EditableSelectComponent
          label="Zonnepanelen aanwezig?"
          editMode={editMode}
          value={roofData.solarPanelsPresent || ""}
          options={Object.values(SolarPanelsPresent).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("solarPanelsPresent", value)}
        />
      ),
    },
    {
      label: "Geschikt om zonnepanelen te leggen?",
      key: "solarPanelsPossible",
      Component: () => (
        <EditableSelectComponent
          label="Geschikt om zonnepanelen te leggen?"
          editMode={editMode}
          value={roofData.solarPanelsPossible || ""}
          options={Object.values(SolarPanelsPossible).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("solarPanelsPossible", value)}
        />
      ),
    },
    {
      label: "Aantal zonnepanelen mogelijk/aanwezig",
      key: "possibleSolarPanels",
    },
    {
      label: "Geïsoleerd",
      key: "insulated",
      Component: () => (
        <EditableSelectComponent
          label="Geïsoleerd"
          editMode={editMode}
          value={roofData.insulated || ""}
          options={Object.values(Insulated).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("insulated", value)}
        />
      ),
    },
  ];

  const roofExtraFieldsConfig = [
    { label: "Aantal dakkapellen", key: "numberOfDormers" },
    { label: "Aantal dakkoepels", key: "numberOfSkylights" },
    { label: "Aantal veluxen", key: "numberOfVeluxWindows" },
    { label: "Aantal schoorstenen", key: "numberOfChimneys" },
    {
      label: "Dakgoot materiaal",
      key: "gutterMaterial",
      Component: () => (
        <EditableSelectComponent
          label="Dakgoot materiaal"
          editMode={editMode}
          value={roofData.gutterMaterial || ""}
          options={Object.values(GutterMaterial).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("gutterMaterial", value)}
        />
      ),
    },
  ];

  const roofInstallerFieldsConfig = [
    {
      label: "Installateur",
      key: "installer",
      Component: createContactFieldComponent(
        "Zoek de installateur in de lijst van installateurs",
        "installer",
        [ResourceType.COMPANY, ResourceType.CONTACT],
        ContactTags.AannemerDakwerken,
      ),
    },
    {
      label: "Opleveringsdatum",
      key: "deliveryDate",
      Component: () => (
        <div className="z-10 flex w-full flex-col gap-1.5">
          <p className="text-xs">Opleveringsdatum</p>
          <DatePicker
            onChange={(date: any) =>
              setRoofData((prev: any) => ({
                ...prev,
                deliveryDate: date,
              }))
            }
            selected={
              roofData.deliveryDate ? new Date(roofData.deliveryDate) : null
            }
            dateFormat="yyyy/MM/dd"
            className="w-full rounded-sm bg-white p-2.5 text-neutral-primary shadow-form-element placeholder:text-neutral-secondary-mid focus:ring-3 focus:ring-opacity-50"
          />
        </div>
      ),
    },
    {
      label: "Volgende inspectiedatum",
      key: "nextInspectionDate",
      Component: () => (
        <div className="z-10 flex w-full flex-col gap-1.5">
          <p className="text-xs">Volgende inspectiedatum</p>
          <DatePicker
            onChange={(date: any) =>
              setRoofData((prev: any) => ({
                ...prev,
                nextInspectionDate: date,
              }))
            }
            selected={
              roofData.nextInspectionDate
                ? new Date(roofData.nextInspectionDate)
                : null
            }
            dateFormat="yyyy/MM/dd"
            className="w-full rounded-sm bg-white p-2.5 text-neutral-primary shadow-form-element placeholder:text-neutral-secondary-mid focus:ring-3 focus:ring-opacity-50"
          />
        </div>
      ),
    },
  ];

  const handleSaveChanges = async () => {
    if (!roofData.mainOrAnnex || !roofData.type || !roofData.subtype) {
      return toast.error(
        "Hoofdak/Zijdak, Type en Subtype zijn verplichte velden",
      );
    }

    (roofData as any).building = { id: building.id };
    try {
      const response = await fetchWithAuth(
        `${API_URL}/building/create-roof`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(roofData),
        },
        session,
      );

      if (response.ok) {
        toast("Roof saved successfully!");
        setRoofData({} as RoofDto);
        onRoofAdded();
        setEditMode(false);
        onClose();
      } else {
        toast("Error saving roof. Please try again.");
      }
    } catch (error) {
      console.error("Failed to save roof: ", error);
      toast("Error saving roof!");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setRoofData({} as RoofDto);
        onClose();
      }}
      size="5xl"
      shouldBlockScroll={true}
      scrollBehavior="inside"
      backdrop="blur"
      className="p-8"
    >
      <ModalContent className="flex flex-col gap-4 font-normal text-neutral-primary">
        <ModalHeader className="flex flex-row items-center justify-between p-0 font-normal">
          {roofData && building ? (
            <p>
              {building?.id} - {roofData?.mainOrAnnex} ({roofData?.type})
              bewerken{" "}
            </p>
          ) : (
            <p>Nieuw dak toevoegen</p>
          )}
          <div className="flex gap-6">
            <ButtonNew
              variant="primaryMoss"
              onClick={() => {
                if (!editMode) setEditMode(true);
                else {
                  handleSaveChanges();
                }
              }}
              icon={editMode ? <Checkmark /> : <Edit />}
              label={editMode ? "Bewaren" : "Bewerken"}
            />
          </div>
        </ModalHeader>
        <ModalBody className="grid gap-5 gap-y-8 px-0 sm:grid-cols-2">
          <div className="flex flex-col gap-2.5">
            {renderSection({
              title: "Algemeen",
              icon: <AlertInfo />,
              fieldsConfig: roofFieldsConfig,
              showEditToggle: false,
              ...sharedRoofProps,
            })}
          </div>
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-2.5">
              {renderSection({
                title: "Extra gegevens",
                icon: <Settings />,
                fieldsConfig: roofExtraFieldsConfig,
                showEditToggle: false,
                ...sharedRoofProps,
              })}
            </div>
            <div className="flex flex-col gap-2.5">
              {renderSection({
                title: "Installatie",
                icon: <Wrench />,
                fieldsConfig: roofInstallerFieldsConfig,
                showEditToggle: false,
                ...sharedRoofProps,
              })}
            </div>
          </div>
          <div className="col-span-full">
            <ImagesContainer
              buildingId={building.id}
              fileLabel={roofData.mainOrAnnex == MainOrAnnex.MAIN ? FileLabel.MainRoofPicture : FileLabel.SideRoofPicture}
              title={`Foto's van het ${roofData.mainOrAnnex == MainOrAnnex.MAIN ? "hoofd" : "zij"}dak`}
            />
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ModalRoof;
